import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
  Tooltip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  Card,
  CardContent,
  Grid,
  Paper,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  VpnKey as VpnKeyIcon,
  Security as SecurityIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Visibility,
  VisibilityOff,
  Key as KeyIcon,
  Shield as ShieldIcon,
  Work as WorkIcon,
  Business as BusinessIcon,
  CorporateFare as CorporateFareIcon,
  CalendarToday as CalendarTodayIcon,
  AccountCircle as AccountCircleIcon,
  Analytics as AnalyticsIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';

import authService from '../shared/services/authService';
import { useTranslation } from '../store/languageStore';

interface UserFormData {
  email: string;
  username: string;
  firstname: string;
  lastname: string;
  phonenumber?: string;
  password?: string;
  is_admin: boolean;
  is_active: boolean;
  isEditing?: boolean;
}

// Schema must use static strings, not tString() as it's evaluated at module load time
const schema = yup.object({
  email: yup.string().email('INVALID_EMAIL').required('EMAIL_REQUIRED'),
  username: yup.string().required('USERNAME_REQUIRED').min(3, 'USERNAME_MIN_LENGTH'),
  firstname: yup.string().required('FIRST_NAME_REQUIRED'),
  lastname: yup.string().required('LAST_NAME_REQUIRED'),
  phonenumber: yup.string().optional(),
  password: yup.string().when('isEditing', {
    is: false,
    then: (schema) => schema.required('PASSWORD_REQUIRED').min(6, 'PASSWORD_MIN_LENGTH'),
    otherwise: (schema) => schema.test('password-optional', 'PASSWORD_MIN_LENGTH', function(value) {
      // Allow empty password when editing (will keep existing password)
      if (!value || value.trim() === '') {
        return true;
      }
      // If password is provided, it must be at least 6 characters
      return value.length >= 6;
    })
  }).optional(),
  is_admin: yup.boolean().required(),
  is_active: yup.boolean().required(),
  isEditing: yup.boolean().optional(),
});

interface UserFormProps {
  open: boolean;
  onClose: (shouldRefresh?: boolean) => void;
  user?: any | null;
  /** When true, this form only manages super admins: forces is_admin=true and hides tenant/role/module controls. */
  superAdminOnly?: boolean;
  /** When true, hides the is_admin ("Administrator privileges") toggle without altering its underlying value. */
  hideAdminToggle?: boolean;
}

const UserForm: React.FC<UserFormProps> = ({ open, onClose, user, superAdminOnly = false, hideAdminToggle = false }) => {
  const theme = useTheme();
  const { tString } = useTranslation();

  // Helper function to translate validation error messages
  const translateError = (error: string | undefined): string | undefined => {
    if (!error) return undefined;
    const errorMap: {[key: string]: string} = {
      'INVALID_EMAIL': tString('admin.userForm.invalidEmail'),
      'EMAIL_REQUIRED': tString('admin.userForm.emailRequired'),
      'USERNAME_REQUIRED': tString('admin.userForm.usernameRequired'),
      'USERNAME_MIN_LENGTH': tString('admin.userForm.usernameMinLength'),
      'FIRST_NAME_REQUIRED': tString('admin.userForm.firstNameRequired'),
      'LAST_NAME_REQUIRED': tString('admin.userForm.lastNameRequired'),
      'PASSWORD_REQUIRED': tString('admin.userForm.passwordRequired'),
      'PASSWORD_MIN_LENGTH': tString('admin.userForm.passwordMinLength'),
    };
    return errorMap[error] || error;
  };

  const [isLoading, setIsLoading] = useState(false);
  const [isTogglingActive, setIsTogglingActive] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [isResettingAuth, setIsResettingAuth] = useState(false);
  const [showAuthResetConfirm, setShowAuthResetConfirm] = useState(false);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isEditingLimits, setIsEditingLimits] = useState(false);
  const [editedMonthlyLimit, setEditedMonthlyLimit] = useState<number | '' | 'unlimited'>('');
  const [isUpdatingLimits, setIsUpdatingLimits] = useState(false);
  const [isEditingResetDate, setIsEditingResetDate] = useState(false);
  const [editedResetDate, setEditedResetDate] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [currentUserData, setCurrentUserData] = useState(user);
  const [activityStats, setActivityStats] = useState<{
    total_documents: number;
    document_processing: number;
    tariff_classification: number;
    email_processing: number;
  } | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const isEditing = !!user;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UserFormData>({
    resolver: yupResolver(schema) as any,
    context: { isEditing },
    defaultValues: {
      email: '',
      username: '',
      firstname: '',
      lastname: '',
      phonenumber: '',
      password: '',
      is_admin: superAdminOnly,
      is_active: true,
      isEditing,
    },
  });

  useEffect(() => {
    if (user) {
      reset({
        email: user.email,
        username: user.username,
        firstname: user.firstname,
        lastname: user.lastname,
        phonenumber: user.phonenumber || '',
        password: '',
        is_admin: superAdminOnly ? true : user.is_admin,
        is_active: user.is_active,
        isEditing: true,
      });
    } else {
      reset({
        email: '',
        username: '',
        firstname: '',
        lastname: '',
        phonenumber: '',
        password: '',
        is_admin: superAdminOnly,
        is_active: true,
        isEditing: false,
      });
    }
  }, [user, superAdminOnly, reset]);

  const onSubmit = async (data: UserFormData) => {
    try {
      setIsLoading(true);
      
      // Remove password from data if editing and password is empty
      const submitData = { ...data };
      if (isEditing && !submitData.password) {
        delete submitData.password;
      }
      delete submitData.isEditing;
      
      if (isEditing) {
        await authService.updateUser(user.id, submitData);
        toast.success(tString('admin.userForm.userUpdated'));
      } else {
        await authService.createUser(submitData);
        toast.success(tString('admin.userForm.userCreated'));
      }

      onClose(true);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || tString('admin.userForm.saveFailed');
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleActiveStatus = async () => {
    if (!user) return;

    try {
      setIsTogglingActive(true);
      const result = await authService.toggleUserActiveStatus(user.id);

      // Update local user data
      const updatedUser = { ...currentUserData, is_active: result.is_active };
      setCurrentUserData(updatedUser);

      // Update form values
      reset({
        ...updatedUser,
        isEditing: true,
        password: '',
      });

      toast.success(result.message);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || tString('admin.userForm.toggleStatusFailed');
      toast.error(errorMessage);
    } finally {
      setIsTogglingActive(false);
    }
  };

  const calculatePasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 15;
    if (/[a-z]/.test(password)) strength += 15;
    if (/[A-Z]/.test(password)) strength += 15;
    if (/[0-9]/.test(password)) strength += 15;
    if (/[^A-Za-z0-9]/.test(password)) strength += 15;
    return Math.min(strength, 100);
  };

  const handlePasswordChange = (password: string) => {
    setNewPassword(password);
    setPasswordStrength(calculatePasswordStrength(password));
  };

  const getPasswordStrengthColor = (strength: number) => {
    if (strength < 40) return 'error';
    if (strength < 70) return 'warning';
    return 'success';
  };

  const getPasswordStrengthLabel = (strength: number) => {
    if (strength < 40) return tString('admin.userForm.weak');
    if (strength < 70) return tString('admin.userForm.medium');
    return tString('admin.userForm.strong');
  };

  const handleResetPassword = () => {
    if (!user) return;
    setShowPasswordResetModal(true);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordStrength(0);
  };

  const handlePasswordResetSubmit = async () => {
    if (!user) return;

    // Validation
    if (newPassword.length < 8) {
      toast.error(tString('admin.userForm.passwordMinLength8'));
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(tString('admin.userForm.passwordsDoNotMatch'));
      return;
    }

    if (passwordStrength < 40) {
      toast.error(tString('admin.userForm.passwordTooWeak'));
      return;
    }

    try {
      setIsResettingPassword(true);
      const result = await authService.resetUserPassword(user.id, newPassword);
      toast.success(result.message);
      setShowPasswordResetModal(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || tString('admin.userForm.resetPasswordFailed');
      toast.error(errorMessage);
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleResetAuthentication = () => {
    if (!user) return;
    setShowAuthResetConfirm(true);
  };

  const confirmResetAuthentication = async () => {
    if (!user) return;

    setShowAuthResetConfirm(false);

    try {
      setIsResettingAuth(true);
      const result = await authService.resetUserAuthentication(user.id);

      toast.success(
        `${result.message}\n${result.details}`,
        { autoClose: 5000 }
      );

      // Show additional info if tokens were found
      if (result.tokens_reset > 0) {
        toast.info(
          `Removed ${result.tokens_reset} authentication token(s) for ${result.user_email}`,
          { autoClose: 3000 }
        );

        // Refresh module authentication status in Document Processing page if it's loaded
        if ((window as any).refreshModuleAuthStatus) {
          try {
            await (window as any).refreshModuleAuthStatus();
            console.log('Module authentication status refreshed after admin reset');
          } catch (refreshError) {
            console.warn('Failed to refresh module auth status:', refreshError);
          }
        }
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || tString('admin.userForm.resetAuthFailed');
      toast.error(errorMessage);
    } finally {
      setIsResettingAuth(false);
    }
  };

  // Update currentUserData when user prop changes
  useEffect(() => {
    setCurrentUserData(user);
  }, [user]);

  // Load activity stats when editing a user
  useEffect(() => {
    if (user?.id) {
      setStatsLoading(true);
      authService.getUserActivityStats(user.id)
        .then((stats) => setActivityStats(stats))
        .catch((err) => console.error('Failed to load activity stats:', err))
        .finally(() => setStatsLoading(false));
    } else {
      setActivityStats(null);
    }
  }, [user?.id]);

  // Initialize edited limit and reset date when user changes
  useEffect(() => {
    if (user) {
      setEditedMonthlyLimit(user.monthly_document_limit || '');
      setIsEditingLimits(false);
      setEditedResetDate(user.monthly_reset_date ? new Date(user.monthly_reset_date).toISOString().split('T')[0] : '');
      setIsEditingResetDate(false);
    }
  }, [user]);

  const handleStartEditingLimits = () => {
    setIsEditingLimits(true);
    setEditedMonthlyLimit(user?.monthly_document_limit || '');
  };

  const handleCancelEditingLimits = () => {
    setIsEditingLimits(false);
    setEditedMonthlyLimit(user?.monthly_document_limit || '');
  };

  const handleSaveLimits = async () => {
    if (!user) return;

    try {
      setIsUpdatingLimits(true);
      const limitValue = editedMonthlyLimit === '' ? null :
                        String(editedMonthlyLimit) === 'unlimited' ? -1 :
                        Number(editedMonthlyLimit);

      await authService.updateUserLimits(user.id, {
        monthly_document_limit: limitValue,
      });

      setCurrentUserData((prev: any) => prev ? {
        ...prev,
        monthly_document_limit: limitValue
      } : null);

      setIsEditingLimits(false);
      toast.success(tString('admin.limitUpdated'));
    } catch (error: any) {
      console.error('Failed to update user limits:', error);
      toast.error(error?.response?.data?.detail || tString('admin.updateLimitsFailed'));
    } finally {
      setIsUpdatingLimits(false);
    }
  };

  const handleExtendLimit = async (additionalLimit: number) => {
    if (!user) return;

    try {
      setIsUpdatingLimits(true);
      const currentLimit = user.monthly_document_limit || 0;
      const newLimit = currentLimit === -1 ? -1 : currentLimit + additionalLimit;

      await authService.updateUserLimits(user.id, {
        monthly_document_limit: newLimit,
      });

      setCurrentUserData((prev: any) => prev ? {
        ...prev,
        monthly_document_limit: newLimit
      } : null);

      setEditedMonthlyLimit(newLimit);
      toast.success(tString('admin.limitExtended', additionalLimit));
    } catch (error: any) {
      console.error('Failed to extend user limit:', error);
      toast.error(error?.response?.data?.detail || tString('admin.extendLimitFailed'));
    } finally {
      setIsUpdatingLimits(false);
    }
  };

  const handleResetUsage = async () => {
    if (!user) return;

    try {
      setIsUpdatingLimits(true);

      await authService.resetUserUsage(user.id);

      setCurrentUserData((prev: any) => prev ? {
        ...prev,
        monthly_documents_used: 0,
        monthly_reset_date: new Date().toISOString()
      } : null);

      toast.success(tString('admin.usageReset'));
    } catch (error: any) {
      console.error('Failed to reset user usage:', error);
      toast.error(error?.response?.data?.detail || tString('admin.resetUsageFailed'));
    } finally {
      setIsUpdatingLimits(false);
    }
  };

  const handleStartEditingResetDate = () => {
    setIsEditingResetDate(true);
    setEditedResetDate(user?.monthly_reset_date ? new Date(user.monthly_reset_date).toISOString().split('T')[0] : '');
  };

  const handleCancelEditingResetDate = () => {
    setIsEditingResetDate(false);
    setEditedResetDate(user?.monthly_reset_date ? new Date(user.monthly_reset_date).toISOString().split('T')[0] : '');
  };

  const handleSaveResetDate = async () => {
    if (!user || !editedResetDate) return;

    try {
      setIsUpdatingLimits(true);
      const resetDate = new Date(editedResetDate).toISOString();

      await authService.updateUserResetDate(user.id, {
        monthly_reset_date: resetDate,
      });

      setCurrentUserData((prev: any) => prev ? {
        ...prev,
        monthly_reset_date: resetDate
      } : null);

      setIsEditingResetDate(false);
      toast.success(tString('admin.resetDateUpdated'));
    } catch (error: any) {
      console.error('Failed to update reset date:', error);
      toast.error(error?.response?.data?.detail || tString('admin.updateResetDateFailed'));
    } finally {
      setIsUpdatingLimits(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {isEditing ? tString('admin.userForm.editUser') : tString('admin.userForm.createUser')}
        <IconButton onClick={() => onClose()} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Basic Information */}
            <Box>
              <Typography variant="h6" gutterBottom>
                {tString('admin.userForm.userInformation')}
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" gap={2}>
                  <Controller
                    name="firstname"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={tString('admin.userForm.firstName')}
                        error={!!errors.firstname}
                        helperText={translateError(errors.firstname?.message)}
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="lastname"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={tString('admin.userForm.lastName')}
                        error={!!errors.lastname}
                        helperText={translateError(errors.lastname?.message)}
                        fullWidth
                      />
                    )}
                  />
                </Box>

                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={tString('admin.userForm.email')}
                      type="email"
                      error={!!errors.email}
                      helperText={translateError(errors.email?.message)}
                      fullWidth
                    />
                  )}
                />

                <Controller
                  name="username"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={tString('admin.userForm.username')}
                      error={!!errors.username}
                      helperText={translateError(errors.username?.message)}
                      fullWidth
                    />
                  )}
                />

                <Controller
                  name="phonenumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={tString('admin.userForm.phoneNumber')}
                      error={!!errors.phonenumber}
                      helperText={translateError(errors.phonenumber?.message)}
                      fullWidth
                    />
                  )}
                />

                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={isEditing ? tString('admin.userForm.newPasswordHint') : tString('admin.userForm.password')}
                      type="password"
                      error={!!errors.password}
                      helperText={translateError(errors.password?.message)}
                      fullWidth
                    />
                  )}
                />

                <Box display="flex" flexDirection="column" gap={1}>
                  {!superAdminOnly && !hideAdminToggle && (
                    <Controller
                      name="is_admin"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={field.value}
                              onChange={field.onChange}
                            />
                          }
                          label={tString('admin.userForm.administratorPrivileges')}
                        />
                      )}
                    />
                  )}

                  {isEditing && (
                    <Box>
                      <Controller
                        name="is_active"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={field.value}
                                onChange={field.onChange}
                                color={field.value ? 'success' : 'error'}
                              />
                            }
                            label={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2">
                                  {field.value ? tString('admin.userForm.activeUser') : tString('admin.userForm.inactiveUser')}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={field.value ? tString('admin.userForm.authenticated') : tString('admin.userForm.blocked')}
                                  color={field.value ? 'success' : 'error'}
                                  variant="outlined"
                                />
                              </Box>
                            }
                          />
                        )}
                      />
                      <Typography variant="caption" color="textSecondary" sx={{ ml: 4, display: 'block' }}>
                        {currentUserData?.is_active
                          ? tString('admin.userForm.userCanLogin')
                          : tString('admin.userForm.userBlocked')
                        }
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            <Divider />

            {/* Authentication Management */}
            {isEditing && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {tString('admin.userForm.authenticationManagement')}
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    backgroundColor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}
                >
                  <SecurityIcon color="primary" />
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {tString('admin.userForm.moduleAuthStatus')}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {tString('admin.userForm.moduleAuthStatusDesc')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Plan Details - Only show when editing */}
            {isEditing && user && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {tString('admin.planDetails')}
                </Typography>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    background: user?.selected_plan === 'enterprise'
                      ? 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)'
                      : user?.selected_plan === 'advanced'
                      ? 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)'
                      : 'linear-gradient(135deg, #F5F5F5 0%, #E0E0E0 100%)',
                    color: user?.selected_plan === 'enterprise' ? '#2e7d32' : user?.selected_plan === 'advanced' ? '#f57c00' : '#6B6B6B',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {user?.selected_plan === 'enterprise' ? (
                        <>
                          <CorporateFareIcon />
                          {tString('admin.enterprise')} Plan
                        </>
                      ) : user?.selected_plan === 'advanced' ? (
                        <>
                          <BusinessIcon />
                          {tString('admin.advanced')} Plan
                        </>
                      ) : (
                        <>
                          <WorkIcon />
                          {tString('admin.essential')} Plan
                        </>
                      )}
                    </Typography>
                    <Chip
                      label={user?.plan_status === 'active' ? tString('admin.active') :
                            user?.plan_status === 'expired' ? tString('admin.expired') :
                            user?.plan_status === 'suspended' ? tString('admin.suspended') : 'Pending'}
                      color={user?.plan_status === 'active' ? 'success' : 'warning'}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        fontWeight: 600,
                      }}
                    />
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card variant="outlined" sx={{ backgroundColor: 'rgba(255,255,255,0.7)' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <CalendarTodayIcon color="primary" sx={{ mb: 1 }} />
                          <Typography variant="body2" color="textSecondary">
                            {tString('admin.planAdopted')}
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {user?.plan_adopted_date ? new Date(user.plan_adopted_date).toLocaleDateString() : 'N/A'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Card variant="outlined" sx={{ backgroundColor: 'rgba(255,255,255,0.7)', position: 'relative' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <AnalyticsIcon color="info" />
                            <IconButton
                              size="small"
                              onClick={isEditingLimits ? handleCancelEditingLimits : handleStartEditingLimits}
                              disabled={isUpdatingLimits}
                              sx={{ p: 0.5 }}
                            >
                              {isEditingLimits ? <CancelIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                            </IconButton>
                          </Box>
                          <Typography variant="body2" color="textSecondary">
                            {tString('admin.monthlyLimit')}
                          </Typography>

                          {isEditingLimits ? (
                            <Box sx={{ mt: 1 }}>
                              <TextField
                                size="small"
                                value={editedMonthlyLimit}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === 'unlimited' || value === '' || (!isNaN(Number(value)) && Number(value) >= 0)) {
                                    if (value === 'unlimited') {
                                      setEditedMonthlyLimit('unlimited');
                                    } else if (value === '') {
                                      setEditedMonthlyLimit('');
                                    } else {
                                      setEditedMonthlyLimit(Number(value));
                                    }
                                  }
                                }}
                                placeholder={tString('admin.enterLimit')}
                                sx={{ width: '120px' }}
                                inputProps={{ style: { textAlign: 'center', fontSize: '14px' } }}
                              />
                              <Box sx={{ display: 'flex', gap: 0.5, mt: 1, justifyContent: 'center' }}>
                                <IconButton
                                  size="small"
                                  onClick={handleSaveLimits}
                                  disabled={isUpdatingLimits}
                                  color="primary"
                                  sx={{ p: 0.5 }}
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          ) : (() => {
                            const used = currentUserData?.monthly_documents_used ?? user?.monthly_documents_used ?? 0;
                            const total = currentUserData?.monthly_document_limit ?? user?.monthly_document_limit;
                            const isUnlimited = total === -1;
                            const pct = isUnlimited ? 0 : Math.min(Math.round((used / (total || 1)) * 100), 100);
                            const color = pct > 80 ? 'error' : pct > 50 ? 'warning' : 'success';
                            return (
                              <Box sx={{ mt: 0.5 }}>
                                <Typography variant="h6" fontWeight={700} color={isUnlimited ? 'success.main' : `${color}.main`}>
                                  {isUnlimited ? '∞' : used.toLocaleString()}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {isUnlimited ? 'Unlimited credits' : `of ${(total ?? 0).toLocaleString()} credits`}
                                </Typography>
                                {!isUnlimited && (
                                  <Box sx={{ mt: 0.75, mx: 'auto', maxWidth: 100 }}>
                                    <Box sx={{ height: 4, borderRadius: 2, bgcolor: 'grey.200', overflow: 'hidden' }}>
                                      <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: `${color}.main`, borderRadius: 2, transition: 'width 0.4s ease' }} />
                                    </Box>
                                    <Typography variant="caption" color={`${color}.main`} fontWeight={600}>
                                      {pct}% used
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            );
                          })()}

                          {!isEditingLimits && (
                            <Box sx={{ mt: 1, display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                              <Button
                                size="small"
                                onClick={() => handleExtendLimit(100)}
                                disabled={isUpdatingLimits}
                                sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '11px' }}
                              >
                                +100
                              </Button>
                              <Button
                                size="small"
                                onClick={() => handleExtendLimit(500)}
                                disabled={isUpdatingLimits}
                                sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '11px' }}
                              >
                                +500
                              </Button>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Card variant="outlined" sx={{ backgroundColor: 'rgba(255,255,255,0.7)' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <AccountCircleIcon color="secondary" sx={{ mb: 1 }} />
                          <Typography variant="body2" color="textSecondary">
                            {tString('admin.documentsUsed')}
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {statsLoading ? '...' : (activityStats?.total_documents ?? 0)}
                          </Typography>
                          <Box sx={{ mt: 1 }}>
                            <Button
                              size="small"
                              onClick={handleResetUsage}
                              disabled={isUpdatingLimits || (user?.monthly_documents_used || 0) === 0}
                              color="warning"
                              sx={{ fontSize: '11px', minWidth: 'auto', px: 1, py: 0.5 }}
                            >
                              Reset
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Card variant="outlined" sx={{ backgroundColor: 'rgba(255,255,255,0.7)', position: 'relative' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <CalendarTodayIcon color="warning" />
                            <IconButton
                              size="small"
                              onClick={isEditingResetDate ? handleCancelEditingResetDate : handleStartEditingResetDate}
                              disabled={isUpdatingLimits}
                              sx={{ p: 0.5 }}
                            >
                              {isEditingResetDate ? <CancelIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                            </IconButton>
                          </Box>
                          <Typography variant="body2" color="textSecondary">
                            {tString('admin.limitResetDate')}
                          </Typography>

                          {isEditingResetDate ? (
                            <Box sx={{ mt: 1 }}>
                              <TextField
                                size="small"
                                type="date"
                                value={editedResetDate}
                                onChange={(e) => setEditedResetDate(e.target.value)}
                                sx={{ width: '130px' }}
                                inputProps={{
                                  style: { textAlign: 'center', fontSize: '12px' },
                                  min: new Date().toISOString().split('T')[0] // Prevent past dates
                                }}
                              />
                              <Box sx={{ display: 'flex', gap: 0.5, mt: 1, justifyContent: 'center' }}>
                                <IconButton
                                  size="small"
                                  onClick={handleSaveResetDate}
                                  disabled={isUpdatingLimits || !editedResetDate}
                                  color="primary"
                                  sx={{ p: 0.5 }}
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          ) : (
                            <Typography variant="body1" fontWeight={500}>
                              {(currentUserData?.monthly_reset_date || user?.monthly_reset_date)
                                ? new Date(currentUserData?.monthly_reset_date || user?.monthly_reset_date).toLocaleDateString()
                                : 'N/A'}
                            </Typography>
                          )}

                          {!isEditingResetDate && (
                            <Box sx={{ mt: 1, display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                              <Button
                                size="small"
                                onClick={async () => {
                                  const nextMonth = new Date();
                                  nextMonth.setMonth(nextMonth.getMonth() + 1);
                                  const nextMonthISO = nextMonth.toISOString().split('T')[0];
                                  setEditedResetDate(nextMonthISO);
                                  await handleSaveResetDate();
                                }}
                                disabled={isUpdatingLimits}
                                sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '10px' }}
                              >
                                {tString('admin.nextMonth')}
                              </Button>
                              <Button
                                size="small"
                                onClick={async () => {
                                  const today = new Date().toISOString().split('T')[0];
                                  setEditedResetDate(today);
                                  await handleSaveResetDate();
                                }}
                                disabled={isUpdatingLimits}
                                sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '10px' }}
                              >
                                {tString('admin.today')}
                              </Button>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Activity Breakdown */}
                  {activityStats && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.8 }}>
                        Activity Breakdown:
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={4}>
                          <Card variant="outlined" sx={{ backgroundColor: 'rgba(255,255,255,0.5)' }}>
                            <CardContent sx={{ textAlign: 'center', py: 1, '&:last-child': { pb: 1 } }}>
                              <Typography variant="caption" color="textSecondary">
                                Document Processing
                              </Typography>
                              <Typography variant="h6" fontWeight={600} color="success.main">
                                {activityStats.document_processing}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={4}>
                          <Card variant="outlined" sx={{ backgroundColor: 'rgba(255,255,255,0.5)' }}>
                            <CardContent sx={{ textAlign: 'center', py: 1, '&:last-child': { pb: 1 } }}>
                              <Typography variant="caption" color="textSecondary">
                                Tariff Classification
                              </Typography>
                              <Typography variant="h6" fontWeight={600} color="warning.main">
                                {activityStats.tariff_classification}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={4}>
                          <Card variant="outlined" sx={{ backgroundColor: 'rgba(255,255,255,0.5)' }}>
                            <CardContent sx={{ textAlign: 'center', py: 1, '&:last-child': { pb: 1 } }}>
                              <Typography variant="caption" color="textSecondary">
                                Email Processing
                              </Typography>
                              <Typography variant="h6" fontWeight={600} color="info.main">
                                {activityStats.email_processing}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {/* Plan Features */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.8 }}>
                      {tString('admin.planFeatures')}:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        size="small"
                        label={tString('admin.basicExtraction')}
                        variant="outlined"
                        sx={{ backgroundColor: 'rgba(255,255,255,0.5)' }}
                      />
                      {user?.has_advanced_extraction && (
                        <Chip
                          size="small"
                          label={tString('admin.advancedAiExtraction')}
                          variant="outlined"
                          sx={{ backgroundColor: 'rgba(255,255,255,0.5)' }}
                        />
                      )}
                      {user?.has_document_tampering_detection && (
                        <Chip
                          size="small"
                          label={tString('admin.tamperingDetection')}
                          variant="outlined"
                          sx={{ backgroundColor: 'rgba(255,255,255,0.5)' }}
                        />
                      )}
                      {user?.has_workflow_automation && (
                        <Chip
                          size="small"
                          label={tString('admin.workflowAutomation')}
                          variant="outlined"
                          sx={{ backgroundColor: 'rgba(255,255,255,0.5)' }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Tariff Feedback feature toggle — only shown in edit mode */}
                  {isEditing && user && (
                    <Box sx={{ mt: 1.5 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            id="tariff-feedback-toggle"
                            checked={!!currentUserData?.has_tariff_feedback}
                            size="small"
                            onChange={async (e) => {
                              const next = e.target.checked;
                              try {
                                await authService.updateUserFeatures(user.id, {
                                  has_tariff_feedback: next,
                                });
                                setCurrentUserData((prev: any) =>
                                  prev ? { ...prev, has_tariff_feedback: next } : null
                                );
                                toast.success(
                                  next
                                    ? 'Tariff feedback enabled'
                                    : 'Tariff feedback disabled'
                                );
                              } catch {
                                toast.error('Failed to update feature flag');
                              }
                            }}
                          />
                        }
                        label={
                          <Typography variant="caption" sx={{ opacity: 0.85 }}>
                            Tariff Feedback
                          </Typography>
                        }
                      />
                    </Box>
                  )}
                </Paper>
              </Box>
            )}

          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
          <Box display="flex" gap={1}>
            {isEditing && currentUserData && (
              <>
                <Tooltip title={currentUserData.is_active ? tString('admin.userForm.deactivateUser') : tString('admin.userForm.activateUser')}>
                  <Button
                    onClick={handleToggleActiveStatus}
                    disabled={isTogglingActive || isLoading}
                    startIcon={currentUserData.is_active ? <LockIcon /> : <LockOpenIcon />}
                    color={currentUserData.is_active ? 'warning' : 'success'}
                    variant="outlined"
                    size="small"
                  >
                    {isTogglingActive ? tString('admin.userForm.processing') : (currentUserData.is_active ? tString('admin.userForm.deactivate') : tString('admin.userForm.activate'))}
                  </Button>
                </Tooltip>

                <Tooltip title={tString('admin.userForm.resetUserPassword')}>
                  <Button
                    onClick={handleResetPassword}
                    disabled={isResettingPassword || isLoading}
                    startIcon={<VpnKeyIcon />}
                    color="info"
                    variant="outlined"
                    size="small"
                  >
                    {isResettingPassword ? tString('admin.userForm.resetting') : tString('admin.userForm.resetPasswordButton')}
                  </Button>
                </Tooltip>

                <Tooltip title={tString('admin.userForm.resetAllModuleAuth')}>
                  <Button
                    onClick={handleResetAuthentication}
                    disabled={isResettingAuth || isLoading}
                    startIcon={<SecurityIcon />}
                    color="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {isResettingAuth ? tString('admin.userForm.resetting') : tString('admin.userForm.resetAuth')}
                  </Button>
                </Tooltip>
              </>
            )}
          </Box>

          <Box display="flex" gap={1}>
            <Button onClick={() => onClose()} disabled={isLoading}>
              {tString('admin.userForm.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
            >
              {isLoading ? tString('admin.userForm.saving') : (isEditing ? tString('admin.userForm.updateUser') : tString('admin.userForm.createNewUser'))}
            </Button>
          </Box>
        </DialogActions>
      </form>

      {/* Authentication Reset Confirmation Dialog */}
      <Dialog
        open={showAuthResetConfirm}
        onClose={() => setShowAuthResetConfirm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <WarningIcon color="warning" />
          <Typography variant="h6">{tString('admin.userForm.resetUserAuthentication')}</Typography>
        </DialogTitle>

        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>{tString('admin.userForm.warning')}</strong> {tString('admin.userForm.resetAuthWarning')}{' '}
              <strong>{user?.email}</strong>
            </Typography>
          </Alert>

          <Typography variant="body2" color="textSecondary" gutterBottom>
            {tString('admin.userForm.performFollowingActions')}
          </Typography>

          <List dense>
            <ListItem>
              <ListItemIcon>
                <SecurityIcon color="error" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={tString('admin.userForm.removeAllTokens')}
                secondary={tString('admin.userForm.userLoseAccess')}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <LockIcon color="error" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={tString('admin.userForm.forceReauth')}
                secondary={tString('admin.userForm.userMustReauth')}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <WarningIcon color="warning" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={tString('admin.userForm.immediateEffect')}
                secondary={tString('admin.userForm.cannotUndone')}
              />
            </ListItem>
          </List>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="caption">
              <strong>{tString('admin.userForm.useCases')}</strong> {tString('admin.userForm.useCasesDesc')}
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={() => setShowAuthResetConfirm(false)}
            variant="outlined"
          >
            {tString('admin.userForm.cancel')}
          </Button>
          <Button
            onClick={confirmResetAuthentication}
            variant="contained"
            color="warning"
            startIcon={<SecurityIcon />}
            disabled={isResettingAuth}
          >
            {isResettingAuth ? tString('admin.userForm.resetting') : tString('admin.userForm.resetAuthentication')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Modal */}
      <Dialog
        open={showPasswordResetModal}
        onClose={() => setShowPasswordResetModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            background: 'linear-gradient(135deg, #FFF8E1 0%, #FFE0B2 100%)',
            color: theme.palette.primary.main,
          },
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          pb: 1,
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(10px)',
          color: 'text.primary',
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
          }}>
            <VpnKeyIcon sx={{ fontSize: 24, color: 'white' }} />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              {tString('admin.userForm.resetPassword')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {user?.email}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setShowPasswordResetModal(false)}
            size="small"
            sx={{ color: 'text.primary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, backgroundColor: 'rgba(255,255,255,0.95)' }}>
          <Alert
            severity="info"
            sx={{
              mb: 3,
              backgroundColor: 'rgba(33, 150, 243, 0.1)',
              color: 'text.primary',
              border: '1px solid rgba(33, 150, 243, 0.2)',
              '& .MuiAlert-icon': { color: theme.palette.primary.main }
            }}
          >
            <Typography variant="body2">
              {tString('admin.userForm.createSecurePassword')} <strong>{user?.firstname} {user?.lastname}</strong>
            </Typography>
          </Alert>

          <Box display="flex" flexDirection="column" gap={3}>
            {/* Password Strength Steps */}
            <Box>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>
                {tString('admin.userForm.passwordRequirements')}
              </Typography>
              <Stepper
                activeStep={Math.floor(passwordStrength / 25)}
                alternativeLabel
                sx={{
                  '& .MuiStepLabel-label': {
                    color: 'text.primary',
                    fontSize: '0.75rem',
                    fontWeight: 500
                  },
                  '& .MuiStepIcon-root': {
                    color: 'rgba(0,0,0,0.2)',
                    '&.Mui-active': { color: theme.palette.primary.main },
                    '&.Mui-completed': { color: '#4caf50' }
                  },
                  '& .MuiStepConnector-line': {
                    borderColor: 'rgba(0,0,0,0.2)'
                  }
                }}
              >
                <Step>
                  <StepLabel>{tString('admin.chars8Plus')}</StepLabel>
                </Step>
                <Step>
                  <StepLabel>{tString('admin.mixedCase')}</StepLabel>
                </Step>
                <Step>
                  <StepLabel>{tString('admin.numbers')}</StepLabel>
                </Step>
                <Step>
                  <StepLabel>{tString('admin.symbols')}</StepLabel>
                </Step>
              </Stepper>
            </Box>

            {/* New Password Field */}
            <TextField
              label={tString('admin.userForm.newPassword')}
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => handlePasswordChange(e.target.value)}
              fullWidth
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  '& fieldset': { borderColor: 'rgba(25, 118, 210, 0.5)' },
                  '&:hover fieldset': { borderColor: 'rgba(25, 118, 210, 0.7)' },
                  '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
                },
                '& .MuiInputLabel-root': {
                  color: 'text.secondary',
                  '&.Mui-focused': { color: theme.palette.primary.main }
                },
                '& .MuiInputBase-input': { color: 'text.primary' }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      sx={{ color: 'text.secondary' }}
                      size="small"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Password Strength Indicator */}
            {newPassword && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {tString('admin.userForm.passwordStrength')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: passwordStrength < 40 ? '#ff5722' :
                             passwordStrength < 70 ? '#ff9800' : '#4caf50'
                    }}
                  >
                    {getPasswordStrengthLabel(passwordStrength)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={passwordStrength}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: passwordStrength < 40 ? '#ff5722' :
                                     passwordStrength < 70 ? '#ff9800' : '#4caf50',
                      borderRadius: 3,
                    },
                  }}
                />
              </Box>
            )}

            {/* Confirm Password Field */}
            <TextField
              label={tString('admin.userForm.confirmPassword')}
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              variant="outlined"
              error={confirmPassword !== '' && newPassword !== confirmPassword}
              helperText={
                confirmPassword !== '' && newPassword !== confirmPassword
                  ? tString('admin.userForm.passwordsDoNotMatch')
                  : ''
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  '& fieldset': {
                    borderColor: confirmPassword !== '' && newPassword !== confirmPassword
                      ? '#ff5722' : 'rgba(25, 118, 210, 0.5)'
                  },
                  '&:hover fieldset': {
                    borderColor: confirmPassword !== '' && newPassword !== confirmPassword
                      ? '#ff5722' : 'rgba(25, 118, 210, 0.7)'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: confirmPassword !== '' && newPassword !== confirmPassword
                      ? '#ff5722' : theme.palette.primary.main
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'text.secondary',
                  '&.Mui-focused': { color: theme.palette.primary.main }
                },
                '& .MuiInputBase-input': { color: 'text.primary' },
                '& .MuiFormHelperText-root': {
                  color: '#ff5722',
                  fontWeight: 500
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      sx={{ color: 'text.secondary' }}
                      size="small"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                    {confirmPassword !== '' && newPassword === confirmPassword && (
                      <CheckCircleIcon sx={{ color: '#4caf50', ml: 1 }} />
                    )}
                  </InputAdornment>
                ),
              }}
            />

            {/* Security Tips */}
            <Alert
              severity="success"
              sx={{
                backgroundColor: 'rgba(76,175,80,0.15)',
                color: 'white',
                '& .MuiAlert-icon': { color: '#4caf50' }
              }}
            >
              <Typography variant="body2">
                <strong>{tString('admin.userForm.securityTips')}</strong> {tString('admin.userForm.securityTipsDesc')}
              </Typography>
            </Alert>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0, backgroundColor: 'rgba(255,255,255,0.95)' }}>
          <Button
            onClick={() => setShowPasswordResetModal(false)}
            variant="outlined"
            sx={{
              color: 'text.secondary',
              borderColor: 'rgba(0,0,0,0.3)',
              '&:hover': {
                borderColor: 'text.primary',
                backgroundColor: 'rgba(0,0,0,0.05)'
              }
            }}
            disabled={isResettingPassword}
          >
            {tString('admin.userForm.cancel')}
          </Button>
          <Button
            onClick={handlePasswordResetSubmit}
            variant="contained"
            startIcon={isResettingPassword ? <CircularProgress size={16} color="inherit" /> : <ShieldIcon />}
            disabled={
              isResettingPassword ||
              !newPassword ||
              !confirmPassword ||
              newPassword !== confirmPassword ||
              passwordStrength < 40
            }
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)'
              },
              '&:disabled': {
                backgroundColor: 'grey.300',
                color: 'grey.500',
                boxShadow: 'none'
              }
            }}
          >
            {isResettingPassword ? tString('admin.userForm.resetting') : tString('admin.userForm.resetPasswordButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default UserForm;